import { ClickableLinkPlugin } from '@lexical/react/LexicalClickableLinkPlugin';
import { Box } from '@mui/material';
import { ComponentType, ReactNode } from 'react';

import { Markdown } from '@/components/Markdown/Markdown';
import { COLORS } from '@/styles/tokens/colors';

interface ChatMarkdownProps {
  markdown: string;
}

function ChatMarkdownInner({ children }: { children: ReactNode }) {
  return (
    <Box
      sx={{
        maxWidth: '100%',
        overflowWrap: 'break-word',
        wordBreak: 'break-word',
        '& p, & li': {
          color: COLORS.TEXT.PRIMARY,
          fontSize: '12px',
          fontWeight: 500,
          whiteSpace: 'pre-wrap',
        },
        '& a': {
          color: COLORS.NAVY[700],
        },
        '& strong': {
          fontWeight: 600,
        },
        '& ul': {
          listStyleType: 'disc',
        },
        // Collapse the generous default table cell padding (12px 8px body /
        // 8px header from the shared TextEditor theme) so chat tables read as
        // compact. Targeting the cell elements outranks the Lexical theme
        // classes by specificity; cell borders are left intact. Body cells
        // get a touch more vertical breathing room than headers.
        '& th': {
          padding: '2px',
        },
        '& td': {
          padding: '4px 2px',
        },
        // The editor's content editable reserves a 150px editing area by
        // default; chat messages should only be as tall as their content.
        '& #text-editor-content-editable': {
          minHeight: '0px !important',
          padding: '0px 10px !important',
        },
        '& #text-editor-content-editable > p': {
          padding: '0 !important',
        },
      }}
    >
      {children}
    </Box>
  );
}

export function ChatMarkdown({ markdown }: ChatMarkdownProps) {
  return (
    <Markdown
      markdown={markdown}
      EditorInner={ChatMarkdownInner as ComponentType<{ children: ReactNode }>}
      preserveNewLines={true}
      // luminary:// internal links are handled by the InternalLinkNode +
      // internalLinkTransformer registered with the core editor (see
      // TextEditor / transformers), so they navigate in-app rather than
      // becoming anchors. ClickableLinkPlugin only governs the remaining
      // (external) links: open them in a new tab instead of navigating away.
      additionalPlugins={<ClickableLinkPlugin newTab />}
    />
  );
}
